class FavoritesManager {
  constructor() {
    this.storageKey = 'shopify_favorites';
    this.favorites = this.loadFavorites();
    this.drawer = null;
    this.init();
  }

  init() {
    this.createDrawer();
    this.bindEvents();
    this.updateFavoritesCount();
    this.updateAllFavoriteButtons();
  }

  // Storage management
  loadFavorites() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return {};
    }
  }

  saveFavorites() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.favorites));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }

  // Product management
  addFavorite(productData) {
    this.favorites[productData.id] = {
      id: productData.id,
      title: productData.title,
      handle: productData.handle,
      price: productData.price,
      comparePrice: productData.comparePrice,
      image: productData.image,
      url: productData.url,
      addedAt: new Date().getTime()
    };
    
    this.saveFavorites();
    this.updateFavoritesCount();
    this.updateFavoriteButton(productData.id, true);
    this.refreshDrawerContent();
    
    // Dispatch event for page updates
    this.dispatchFavoritesChanged();
    
    // Show feedback
    this.showNotification(`${productData.title} added to favorites`);
  }

  removeFavorite(productId) {
    if (this.favorites[productId]) {
      const productTitle = this.favorites[productId].title;
      delete this.favorites[productId];
      this.saveFavorites();
      this.updateFavoritesCount();
      this.updateFavoriteButton(productId, false);
      this.refreshDrawerContent();
      
      // Dispatch event for page updates
      this.dispatchFavoritesChanged();
      
      // Show feedback
      this.showNotification(`${productTitle} removed from favorites`);
    }
  }

  isFavorite(productId) {
    return !!this.favorites[productId];
  }

  getFavoritesList() {
    return Object.values(this.favorites).sort((a, b) => b.addedAt - a.addedAt);
  }

  getFavoritesCount() {
    return Object.keys(this.favorites).length;
  }

  // Generate favorites page URL with product handles
  getFavoritesPageUrl() {
    const favoritesList = this.getFavoritesList();
    const handles = favoritesList
      .filter(fav => fav.handle)
      .map(fav => fav.handle);
    
    if (handles.length === 0) {
      return '/pages/my-favorites';
    }
    
    const handlesParam = handles.join(',');
    return `/pages/my-favorites?handles=${encodeURIComponent(handlesParam)}`;
  }

  // UI Management
  updateFavoritesCount() {
    const count = this.getFavoritesCount();
    const countElements = document.querySelectorAll('.favorites-count-bubble');
    
    countElements.forEach(element => {
      if (count > 0) {
        element.style.display = 'flex';
        element.textContent = count > 99 ? '99+' : count.toString();
      } else {
        element.style.display = 'none';
      }
    });
  }

  updateFavoriteButton(productId, isFavorite) {
    const buttons = document.querySelectorAll(`[data-product-id="${productId}"]`);
    buttons.forEach(button => {
      if (isFavorite) {
        button.classList.add('card__favorite-button--active');
        button.setAttribute('aria-label', 'Remove from favorites');
        button.setAttribute('title', 'Remove from favorites');
      } else {
        button.classList.remove('card__favorite-button--active');
        button.setAttribute('aria-label', 'Add to favorites');
        button.setAttribute('title', 'Add to favorites');
      }
    });
  }

  updateAllFavoriteButtons() {
    const buttons = document.querySelectorAll('.card__favorite-button');
    buttons.forEach(button => {
      const productId = button.getAttribute('data-product-id');
      if (productId) {
        this.updateFavoriteButton(productId, this.isFavorite(productId));
      }
    });
  }

  // Drawer management
  createDrawer() {
    const existingDrawer = document.querySelector('.favorites-drawer-container');
    if (existingDrawer) {
      this.drawer = existingDrawer;
      this.refreshDrawerContent(); // Update footer visibility
      return;
    }

    this.drawer = document.createElement('div');
    this.drawer.className = 'favorites-drawer-container';
    this.drawer.innerHTML = this.getDrawerHTML();
    document.body.appendChild(this.drawer);

    this.bindDrawerEvents();
    this.refreshDrawerContent(); // Set initial footer visibility
  }

  getDrawerHTML() {
    return `
      <div class="favorites-drawer__overlay"></div>
      <div class="favorites-drawer">
        <div class="favorites-drawer__header">
          <h2 class="favorites-drawer__title">My Favorites</h2>
          <button class="favorites-drawer__close" aria-label="Close favorites">
            <svg class="icon" viewBox="0 0 20 20">
              <path d="M15.89 4.11a1 1 0 1 0-1.42-1.42L10 7.17 5.53 2.69a1 1 0 0 0-1.42 1.42L8.59 8.6 4.11 13.09a1 1 0 1 0 1.42 1.42L10 10.02l4.47 4.49a1 1 0 0 0 1.42-1.42L11.41 8.6z"/>
            </svg>
          </button>
        </div>
        <div class="favorites-drawer__content">
          ${this.getDrawerContentHTML()}
        </div>
        <div class="favorites-drawer__footer" style="display: none;">
          <a href="${this.getFavoritesPageUrl()}" class="favorites-drawer__view-all">
            <span>View All Favorites</span>
            <svg class="icon" viewBox="0 0 20 20">
              <path d="M6 10l4-4 4 4-1.5 1.5L10 9l-2.5 2.5z" transform="rotate(90 10 10)"/>
            </svg>
          </a>
        </div>
      </div>
    `;
  }

  getDrawerContentHTML() {
    const favoritesList = this.getFavoritesList();
    
    if (favoritesList.length === 0) {
      return `
        <div class="favorites-drawer__empty">
          <svg class="favorites-drawer__empty-icon" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/>
          </svg>
          <p class="favorites-drawer__empty-text">No favorites yet</p>
          <p class="favorites-drawer__empty-subtext">Save your favorite products to see them here</p>
        </div>
      `;
    }

    const itemsHTML = favoritesList.map(favorite => this.getFavoriteItemHTML(favorite)).join('');
    
    return `
      <div class="favorites-list">
        ${itemsHTML}
      </div>
    `;
  }

  getFavoriteItemHTML(favorite) {
    return `
      <div class="favorites-item" data-product-id="${favorite.id}">
        <div class="favorites-item__image">
          <img src="${favorite.image}" alt="${favorite.title}" loading="lazy">
        </div>
        <div class="favorites-item__content">
          <a href="${favorite.url}" class="favorites-item__title">${favorite.title}</a>
        </div>
        <button class="favorites-item__remove" onclick="window.favoritesManager.removeFavorite('${favorite.id}')" aria-label="Remove ${favorite.title} from favorites">
          <svg class="icon" viewBox="0 0 20 20">
            <path d="M15.89 4.11a1 1 0 1 0-1.42-1.42L10 7.17 5.53 2.69a1 1 0 0 0-1.42 1.42L8.59 8.6 4.11 13.09a1 1 0 1 0 1.42 1.42L10 10.02l4.47 4.49a1 1 0 0 0 1.42-1.42L11.41 8.6z"/>
          </svg>
        </button>
      </div>
    `;
  }

  formatPrice(price, comparePrice) {
    if (comparePrice && comparePrice !== price) {
      return `<span class="price--on-sale">${price}</span> <s class="price--compare">${comparePrice}</s>`;
    }
    return `<span class="price">${price}</span>`;
  }

  refreshDrawerContent() {
    const contentElement = this.drawer?.querySelector('.favorites-drawer__content');
    const footerElement = this.drawer?.querySelector('.favorites-drawer__footer');
    
    if (contentElement) {
      // Clear any potential style conflicts
      contentElement.style.background = '';
      contentElement.style.color = '';
      contentElement.innerHTML = this.getDrawerContentHTML();
    }
    
    // Show/hide footer based on favorites count
    if (footerElement) {
      const favoritesCount = this.getFavoritesCount();
      footerElement.style.display = favoritesCount > 0 ? 'block' : 'none';
      // Ensure footer background is correct
      footerElement.style.background = '';
      footerElement.style.color = '';
    }
    
    // Force a repaint to prevent visual glitches
    if (this.drawer) {
      this.drawer.style.display = 'none';
      void this.drawer.offsetHeight; // Force reflow
      this.drawer.style.display = '';
    }
  }

  openDrawer() {
    if (this.drawer) {
      this.drawer.classList.add('is-open');
      document.body.classList.add('favorites-drawer-open');
      
      // Focus trap
      const closeButton = this.drawer.querySelector('.favorites-drawer__close');
      if (closeButton) {
        closeButton.focus();
      }
    }
  }

  closeDrawer() {
    if (this.drawer) {
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('favorites-drawer-open');
    }
  }

  // Event handling
  bindEvents() {
    // Handle favorite button clicks
    document.addEventListener('click', this.handleFavoriteButtonClick.bind(this));
    
    // Handle favorites icon clicks
    document.addEventListener('click', this.handleFavoritesIconClick.bind(this));
    
    // Handle outside clicks to close drawer
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    
    // Handle keyboard events
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  bindDrawerEvents() {
    if (!this.drawer) return;

    // Close drawer events
    const overlay = this.drawer.querySelector('.favorites-drawer__overlay');
    const closeButton = this.drawer.querySelector('.favorites-drawer__close');
    
    if (overlay) {
      overlay.addEventListener('click', () => this.closeDrawer());
    }
    
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeDrawer());
    }
  }

  handleFavoriteButtonClick(event) {
    if (!event.target.closest('.card__favorite-button')) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.target.closest('.card__favorite-button');
    const productData = this.extractProductData(button);
    
    if (!productData) {
      console.error('Could not extract product data from button');
      return;
    }

    // Add pulse animation
    button.classList.add('card__favorite-button--adding');
    setTimeout(() => {
      button.classList.remove('card__favorite-button--adding');
    }, 600);

    if (this.isFavorite(productData.id)) {
      this.removeFavorite(productData.id);
    } else {
      this.addFavorite(productData);
    }
  }

  handleFavoritesIconClick(event) {
    if (!event.target.closest('.header__icon--favorites')) return;
    
    event.preventDefault();
    this.openDrawer();
  }

  handleOutsideClick(event) {
    // Only handle when drawer is open
    if (!this.drawer?.classList.contains('is-open')) return;
    
    // Don't close if clicking on favorites icon (opening trigger)
    if (event.target.closest('.header__icon--favorites')) return;
    
    // Don't close if clicking inside the drawer panel
    if (event.target.closest('.favorites-drawer')) return;
    
    // Close drawer for any other outside clicks
    this.closeDrawer();
  }

  handleKeydown(event) {
    // Close drawer with Escape key
    if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
      this.closeDrawer();
    }
  }

  extractProductData(button) {
    const productId = button.getAttribute('data-product-id');
    const title = button.getAttribute('data-product-title');
    const handle = button.getAttribute('data-product-handle');
    const price = button.getAttribute('data-product-price');
    const comparePrice = button.getAttribute('data-product-compare-price');
    const image = button.getAttribute('data-product-image');
    const url = button.getAttribute('data-product-url');

    if (!productId || !title) {
      return null;
    }

    return {
      id: productId,
      title: title,
      handle: handle || '',
      price: price || '',
      comparePrice: comparePrice || '',
      image: image || '',
      url: url || '#'
    };
  }

  // Notifications
  showNotification(message) {
    // Create a simple toast notification
    const notification = document.createElement('div');
    notification.className = 'favorites-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 2rem;
      right: 2rem;
      background: rgb(var(--color-foreground));
      color: rgb(var(--color-background));
      padding: 1rem 2rem;
      border-radius: 0.5rem;
      z-index: 1000;
      font-size: 1.4rem;
      transform: translateX(100%);
      transition: transform 0.3s ease;
      max-width: 30rem;
    `;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    // Remove after delay
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  // Event dispatching
  dispatchFavoritesChanged() {
    const event = new CustomEvent('favoritesChanged', {
      detail: {
        favorites: this.favorites,
        count: this.getFavoritesCount()
      }
    });
    document.dispatchEvent(event);
  }

  // Public API methods
  clearAllFavorites() {
    if (confirm('Are you sure you want to remove all favorites?')) {
      this.favorites = {};
      this.saveFavorites();
      this.updateFavoritesCount();
      this.updateAllFavoriteButtons();
      this.refreshDrawerContent();
      this.dispatchFavoritesChanged();
      this.showNotification('All favorites cleared');
    }
  }

  exportFavorites() {
    const favoritesList = this.getFavoritesList();
    const dataStr = JSON.stringify(favoritesList, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = 'my-favorites.json';
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.favoritesManager = new FavoritesManager();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.favoritesManager = new FavoritesManager();
  });
} else {
  window.favoritesManager = new FavoritesManager();
}